import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { X, AlertTriangle, MessageSquare } from "lucide-react";

function ConfirmModal({ title, message, confirmLabel, cancelLabel, danger, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose(false);
      if (e.key === "Enter") onClose(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/80 flex items-center justify-center p-4"
      onClick={() => onClose(false)}
      data-testid="confirm-overlay"
    >
      <div
        className="bg-[#050805] border border-[#2D7544] w-full max-w-md shadow-[6px_6px_0px_#00FF41]"
        onClick={(e) => e.stopPropagation()}
        data-testid="confirm-dialog"
      >
        <div className="flex items-center justify-between border-b border-[#2D7544] px-4 py-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className={danger ? "text-[#FF3344]" : "text-[#FFB100]"} />
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#66B07A]">
              {danger ? "destructive action" : "confirm action"}
            </div>
          </div>
          <button onClick={() => onClose(false)} className="p-1 hover:bg-[#0D1F12]" data-testid="confirm-close">
            <X size={14} />
          </button>
        </div>
        <div className="p-5">
          <div className="font-bold text-base text-[#00FF41] mb-2">{title}</div>
          {message && (
            <div className="text-[12px] text-[#66B07A] leading-relaxed font-mono whitespace-pre-wrap break-words">
              {message}
            </div>
          )}
        </div>
        <div className="border-t border-[#2D7544] px-4 py-3 flex justify-end gap-2">
          <button onClick={() => onClose(false)} className="dv-btn-secondary" data-testid="confirm-cancel">
            {cancelLabel}
          </button>
          <button
            onClick={() => onClose(true)}
            className={
              danger
                ? "bg-transparent text-[#FF3344] border border-[#FF3344] px-3 py-2 font-bold uppercase text-[11px] tracking-[0.18em] hover:bg-[#FF3344] hover:text-black transition-colors"
                : "dv-btn-primary"
            }
            data-testid="confirm-ok"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function PromptModal({ title, message, defaultValue, confirmLabel, cancelLabel, placeholder, onClose }) {
  const inputId = "dv-prompt-input";

  useEffect(() => {
    const el = document.getElementById(inputId);
    if (el) {
      el.value = defaultValue || "";
      el.focus();
      el.select?.();
    }
    const onKey = (e) => {
      if (e.key === "Escape") onClose(null);
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        const v = document.getElementById(inputId)?.value || "";
        onClose(v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [defaultValue, onClose]);

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/80 flex items-center justify-center p-4"
      onClick={() => onClose(null)}
      data-testid="prompt-overlay"
    >
      <div
        className="bg-[#050805] border border-[#2D7544] w-full max-w-md shadow-[6px_6px_0px_#00FF41]"
        onClick={(e) => e.stopPropagation()}
        data-testid="prompt-dialog"
      >
        <div className="flex items-center justify-between border-b border-[#2D7544] px-4 py-3">
          <div className="flex items-center gap-2">
            <MessageSquare size={14} className="text-[#00FF41]" />
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#66B07A]">input required</div>
          </div>
          <button onClick={() => onClose(null)} className="p-1 hover:bg-[#0D1F12]" data-testid="prompt-close">
            <X size={14} />
          </button>
        </div>
        <div className="p-5 flex flex-col gap-3">
          <div className="font-bold text-base text-[#00FF41]">{title}</div>
          {message && (
            <div className="text-[12px] text-[#66B07A] leading-relaxed">{message}</div>
          )}
          <input
            id={inputId}
            data-testid="prompt-input"
            defaultValue={defaultValue || ""}
            placeholder={placeholder}
            className="dv-input w-full"
          />
        </div>
        <div className="border-t border-[#2D7544] px-4 py-3 flex justify-end gap-2">
          <button onClick={() => onClose(null)} className="dv-btn-secondary" data-testid="prompt-cancel">
            {cancelLabel}
          </button>
          <button
            onClick={() => onClose(document.getElementById(inputId)?.value || "")}
            className="dv-btn-primary"
            data-testid="prompt-ok"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function mountAndAwait(renderFn) {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    host.dataset.dvDialog = "true";
    document.body.appendChild(host);
    const root = createRoot(host);
    const close = (value) => {
      try { root.unmount(); } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[dv] unmount swallowed:", err); }
      host.remove();
      resolve(value);
    };
    root.render(renderFn(close));
  });
}

export function confirmDialog({
  title,
  message = "",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
} = {}) {
  return mountAndAwait((close) => (
    <ConfirmModal
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      danger={danger}
      onClose={close}
    />
  ));
}

export function promptDialog({
  title,
  message = "",
  defaultValue = "",
  placeholder = "",
  confirmLabel = "OK",
  cancelLabel = "Cancel",
} = {}) {
  return mountAndAwait((close) => (
    <PromptModal
      title={title}
      message={message}
      defaultValue={defaultValue}
      placeholder={placeholder}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      onClose={close}
    />
  ));
}
